/**
 * Widget chat panel — renders the chat UI inside the closed shadow root
 * once a widget session has been issued by the API.
 *
 * Phase 1B Slice 4: header + message list + composer + send. Pure DOM,
 * no framework. The panel does not know about sockets — it surfaces
 * submission via {@link MountPanelOptions.onSend} so the caller can wire
 * the realtime transport.
 *
 * All styles are scoped to the shadow root via a single `<style>` element.
 */

import type { ChatMessageDto } from '@prochat/shared'
import { DEFAULT_THEME, type WidgetTheme, type WidgetPosition } from './theme'

/**
 * Guard a tenant-supplied media URL before it reaches an `src`. Mirrors
 * `sanitizeMediaUrl` in widget-bridge: http(s) and root-relative `/media/`
 * only, so `javascript:`, `data:` and protocol-relative `//host` are dropped.
 */
function sanitizeMediaUrl(value: string | undefined): string | null {
  if (!value) return null
  const v = value.trim()
  if (!v) return null
  if (v.startsWith('/media/')) return v
  if (/^https?:\/\//i.test(v)) return v
  return null
}

export type PanelConnectionState = 'connecting' | 'connected' | 'disconnected' | 'error'

export interface PanelController {
  /** Render a message into the timeline (incoming or echo). */
  appendMessage(message: ChatMessageDto): void
  /** Render a system notice line (e.g. "Operator joined"). */
  appendSystemNotice(text: string): void
  /**
   * Mark an optimistic visitor message as failed (send was nacked or the
   * ack timed out). Renders a "Gửi thất bại · Gửi lại" row; clicking
   * "Gửi lại" removes the failed node and re-submits via onSend.
   */
  markMessageFailed(externalMessageId: string): void
  /** Reflect realtime connection state in the header indicator. */
  setConnectionState(state: PanelConnectionState): void
  /** Remove all listeners and DOM nodes (panel closed). */
  destroy(): void
}

export interface MountPanelOptions {
  /** Initial messages to render on mount (e.g. REST history). */
  initialMessages?: ReadonlyArray<ChatMessageDto>
  /** Welcome notice shown when there are no messages. */
  welcomeMessage?: string
  /** Starter questions shown before freeform chat begins. */
  quickQuestions?: ReadonlyArray<string>
  /** Locale string for timestamps. Defaults to 'vi-VN'. */
  locale?: string
  /** Called when the user submits a non-empty message. */
  onSend?: (content: string) => void
  /** Called when the user clicks the close (×) button. */
  onClose?: () => void
  /**
   * Branding theme. Falls back to DEFAULT_THEME when omitted so
   * embed sites that don't read branding from the session response
   * still get a sensible look.
   */
  theme?: WidgetTheme
  /** Title shown in the header. Falls back to appName / 'ProChat'. */
  title?: string
  /**
   * Small round image left of the title. Falls back to the theme's
   * `panelAvatarUrl`, then to no image at all.
   */
  avatarUrl?: string
}

/**
 * Mount the chat panel inside the supplied shadow root.
 *
 * The returned controller is purely imperative. The caller is responsible
 * for wiring socket events to `appendMessage` and `setConnectionState`.
 *
 * The composer is a real `<form>` so it works on mobile devices' native
 * "Go" key and supports submit-via-Enter without any custom keyboard logic.
 */
export function mountPanel(
  shadow: ShadowRoot,
  opts: MountPanelOptions = {},
): PanelController {
  const locale = opts.locale ?? 'vi-VN'
  const theme: WidgetTheme = opts.theme ?? DEFAULT_THEME
  const initialMessages = opts.initialMessages ?? []
  const quickQuestions = (opts.quickQuestions ?? []).filter((question) => question.trim().length > 0)
  const shouldShowQuickQuestions = initialMessages.length === 0 && quickQuestions.length > 0

  const style = document.createElement('style')
  style.textContent = panelCss(theme.primary, theme.radius, theme.position)
  shadow.appendChild(style)

  const root = document.createElement('div')
  root.className = 'pc-panel'
  root.setAttribute('role', 'dialog')
  root.setAttribute('aria-label', 'ProChat')
  shadow.appendChild(root)

  // Header
  const header = document.createElement('div')
  header.className = 'pc-header'
  const titleRow = document.createElement('div')
  titleRow.className = 'pc-title-row'
  // Tenant-supplied image, so it goes through the same URL guard as the
  // launcher avatar. An avatar that 404s must not push the title out of
  // the header, hence the error handler that removes just the <img>.
  const avatarSrc = sanitizeMediaUrl(opts.avatarUrl ?? theme.panelAvatarUrl)
  if (avatarSrc) {
    const avatar = document.createElement('img')
    avatar.className = 'pc-avatar'
    avatar.src = avatarSrc
    avatar.alt = ''
    avatar.setAttribute('aria-hidden', 'true')
    avatar.addEventListener('error', () => avatar.remove())
    titleRow.appendChild(avatar)
  }
  const title = document.createElement('div')
  title.className = 'pc-title'
  title.textContent = opts.title ?? 'ProChat'
  const status = document.createElement('span')
  status.className = 'pc-status pc-status--connecting'
  status.dataset['state'] = 'connecting'
  status.textContent = stateLabel('connecting')
  titleRow.appendChild(title)
  titleRow.appendChild(status)
  const closeBtn = document.createElement('button')
  closeBtn.type = 'button'
  closeBtn.className = 'pc-close'
  closeBtn.setAttribute('aria-label', 'Đóng')
  closeBtn.textContent = '×'
  header.appendChild(titleRow)
  header.appendChild(closeBtn)
  root.appendChild(header)

  // Messages list
  const list = document.createElement('div')
  list.className = 'pc-list'
  list.setAttribute('aria-live', 'polite')
  root.appendChild(list)

  if (initialMessages.length === 0 && opts.welcomeMessage) {
    list.appendChild(buildSystemNode(opts.welcomeMessage))
  } else {
    for (const m of initialMessages) {
      list.appendChild(renderMessage(m, locale, opts.onSend))
    }
  }

  let destroyed = false
  let quickQuestionsWrap: HTMLElement | null = null
  let quickQuestionsHint: HTMLElement | null = null

  if (shouldShowQuickQuestions) {
    quickQuestionsHint = document.createElement('div')
    quickQuestionsHint.className = 'pc-system pc-system--hint'
    quickQuestionsHint.textContent = 'Chọn câu hỏi để bắt đầu trò chuyện với AI.'
    list.appendChild(quickQuestionsHint)

    quickQuestionsWrap = document.createElement('div')
    quickQuestionsWrap.className = 'pc-quick-questions'
    for (const question of quickQuestions) {
      const button = document.createElement('button')
      button.type = 'button'
      button.className = 'pc-quick-question'
      button.textContent = question
      button.addEventListener('click', () => {
        if (destroyed) return
        hideQuickQuestions()
        if (opts.onSend) opts.onSend(question)
      })
      quickQuestionsWrap.appendChild(button)
    }
    list.appendChild(quickQuestionsWrap)
  }

  const composerHint = document.createElement('div')
  composerHint.className = 'pc-composer-hint'
  composerHint.textContent = 'Chọn một câu hỏi mở đầu để bắt đầu.'
  composerHint.hidden = !shouldShowQuickQuestions
  root.appendChild(composerHint)

  // Composer
  const composer = document.createElement('form')
  composer.className = 'pc-composer'
  composer.setAttribute('aria-label', 'Gửi tin nhắn')

  const input = document.createElement('input')
  input.type = 'text'
  input.className = 'pc-input'
  input.placeholder = 'Nhập tin nhắn…'
  input.maxLength = 4000
  input.setAttribute('aria-label', 'Tin nhắn')

  const sendBtn = document.createElement('button')
  sendBtn.type = 'submit'
  sendBtn.className = 'pc-send'
  sendBtn.textContent = 'Gửi'

  composer.appendChild(input)
  composer.appendChild(sendBtn)
  root.appendChild(composer)

  function hideQuickQuestions() {
    quickQuestionsWrap?.remove()
    quickQuestionsWrap = null
    quickQuestionsHint?.remove()
    quickQuestionsHint = null
    composer.classList.remove('pc-composer--disabled')
    composer.removeAttribute('aria-disabled')
    composerHint.hidden = true
    input.disabled = false
    sendBtn.disabled = false
  }

  function lockComposerUntilQuestionPick() {
    composer.classList.add('pc-composer--disabled')
    composer.setAttribute('aria-disabled', 'true')
    input.disabled = true
    sendBtn.disabled = true
  }

  if (shouldShowQuickQuestions) lockComposerUntilQuestionPick()

  function setState(state: PanelConnectionState) {
    if (destroyed) return
    status.dataset['state'] = state
    status.className = `pc-status pc-status--${state}`
    status.textContent = stateLabel(state)
  }

  function appendMessage(message: ChatMessageDto) {
    if (destroyed) return
    hideQuickQuestions()
    const nextNode = renderMessage(message, locale, opts.onSend)
    const existing = findExistingMessageNode(list, message)
    if (existing) {
      existing.replaceWith(nextNode)
    } else {
      list.appendChild(nextNode)
    }
    list.scrollTop = list.scrollHeight
  }

  function appendSystem(text: string) {
    if (destroyed) return
    list.appendChild(buildSystemNode(text))
    list.scrollTop = list.scrollHeight
  }

  function markMessageFailed(externalMessageId: string) {
    if (destroyed) return
    const escaped = escapeAttributeValue(externalMessageId)
    const node = list.querySelector(`.pc-msg[data-external-message-id="${escaped}"]`)
    if (!(node instanceof HTMLElement)) return
    // Idempotent: a second nack for the same message must not stack rows.
    if (node.querySelector('.pc-retry')) return
    node.classList.add('pc-msg--failed')
    const content = node.querySelector('.pc-bubble')?.textContent ?? ''
    const row = document.createElement('div')
    row.className = 'pc-retry'
    const label = document.createElement('span')
    label.className = 'pc-retry-label'
    label.textContent = 'Gửi thất bại.'
    const retryBtn = document.createElement('button')
    retryBtn.type = 'button'
    retryBtn.className = 'pc-retry-btn'
    retryBtn.textContent = 'Gửi lại'
    retryBtn.addEventListener('click', () => {
      if (destroyed || !content) return
      node.remove()
      if (opts.onSend) opts.onSend(content)
    })
    row.appendChild(label)
    row.appendChild(retryBtn)
    node.appendChild(row)
    list.scrollTop = list.scrollHeight
  }

  function onSubmit(e: Event) {
    e.preventDefault()
    if (composer.getAttribute('aria-disabled') === 'true') return
    const value = input.value
    const trimmed = value.trim()
    if (!trimmed || destroyed) return
    input.value = ''
    if (opts.onSend) opts.onSend(trimmed)
  }

  function onCloseClick() {
    if (!destroyed && opts.onClose) opts.onClose()
  }

  composer.addEventListener('submit', onSubmit)
  closeBtn.addEventListener('click', onCloseClick)

  return {
    appendMessage,
    appendSystemNotice: appendSystem,
    markMessageFailed,
    setConnectionState: setState,
    destroy() {
      if (destroyed) return
      destroyed = true
      composer.removeEventListener('submit', onSubmit)
      closeBtn.removeEventListener('click', onCloseClick)
      root.remove()
      style.remove()
    },
  }
}

function buildSystemNode(text: string): HTMLElement {
  const el = document.createElement('div')
  el.className = 'pc-system'
  el.textContent = text
  return el
}

/**
 * Render one message bubble. When the message carries a `quick_replies`
 * attachment (written by the flow engine) a row of tappable choices is
 * appended under the bubble; picking one sends its value as a normal
 * visitor message and retires the row so the choice cannot be sent twice.
 */
function renderMessage(
  message: ChatMessageDto,
  locale: string,
  onSend?: (content: string) => void,
): HTMLElement {
  const el = document.createElement('div')
  el.className = `pc-msg pc-msg--${message.senderType}`
  el.dataset['msgId'] = message.id
  if (message.externalMessageId) {
    el.dataset['externalMessageId'] = message.externalMessageId
  }
  const bubble = document.createElement('div')
  bubble.className = 'pc-bubble'
  bubble.textContent = message.content ?? ''
  const meta = document.createElement('div')
  meta.className = 'pc-meta'
  meta.textContent = formatTime(message.createdAt, locale)
  el.appendChild(bubble)
  el.appendChild(meta)

  const buttons = quickReplyButtons(message.attachments)
  if (buttons.length > 0) {
    const wrap = document.createElement('div')
    wrap.className = 'pc-quick-replies'
    for (const button of buttons) {
      const btn = document.createElement('button')
      btn.type = 'button'
      btn.className = 'pc-quick-reply'
      btn.textContent = button.label
      btn.addEventListener('click', () => {
        if (!onSend) return
        // Retire the row first: re-clicking a button that is about to be
        // answered would send the same choice twice.
        wrap.remove()
        onSend(button.value)
      })
      wrap.appendChild(btn)
    }
    el.appendChild(wrap)
  }
  return el
}

interface QuickReplyButton {
  label: string
  value: string
}

/**
 * Pull `quick_replies` buttons out of a message's `attachments` blob. The
 * engine writes `[{ type: 'quick_replies', buttons: [{ label, value }] }]`;
 * anything else (or a malformed shape) is ignored rather than throwing,
 * since attachments are free-form jsonb that a human editor can fill in.
 */
function quickReplyButtons(attachments: unknown): QuickReplyButton[] {
  if (!Array.isArray(attachments)) return []
  const entry = attachments.find(
    (a): a is { type?: string; buttons?: unknown } =>
      typeof a === 'object' && a !== null && (a as { type?: string }).type === 'quick_replies',
  )
  const raw = entry?.buttons
  if (!Array.isArray(raw)) return []
  const out: QuickReplyButton[] = []
  for (const item of raw) {
    if (typeof item === 'string' && item.trim()) {
      out.push({ label: item, value: item })
      continue
    }
    if (typeof item === 'object' && item !== null) {
      const label = (item as { label?: unknown }).label
      const value = (item as { value?: unknown }).value
      if (typeof label === 'string' && label.trim()) {
        out.push({ label, value: typeof value === 'string' && value.trim() ? value : label })
      }
    }
  }
  return out
}

function findExistingMessageNode(list: HTMLElement, message: ChatMessageDto): HTMLElement | null {
  if (message.externalMessageId) {
    const escapedExternalMessageId = escapeAttributeValue(message.externalMessageId)
    const byExternalMessageId = list.querySelector(
      `.pc-msg[data-external-message-id="${escapedExternalMessageId}"]`,
    )
    if (byExternalMessageId instanceof HTMLElement) {
      return byExternalMessageId
    }
  }
  const byMessageId = list.querySelector(`.pc-msg[data-msg-id="${escapeAttributeValue(message.id)}"]`)
  return byMessageId instanceof HTMLElement ? byMessageId : null
}

function escapeAttributeValue(value: string): string {
  if (typeof CSS !== 'undefined' && typeof CSS.escape === 'function') {
    return CSS.escape(value)
  }
  return value.replace(/\\/g, '\\\\').replace(/"/g, '\\"')
}

function formatTime(iso: string, locale: string): string {
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return ''
  try {
    return d.toLocaleTimeString(locale, { hour: '2-digit', minute: '2-digit' })
  } catch {
    return d.toISOString().slice(11, 16)
  }
}

function stateLabel(state: PanelConnectionState): string {
  switch (state) {
    case 'connecting':
      return 'Đang kết nối…'
    case 'connected':
      return 'Trực tuyến'
    case 'disconnected':
      return 'Mất kết nối'
    case 'error':
      return 'Lỗi kết nối'
  }
}

function panelCss(primary: string, radius: number, position: WidgetPosition): string {
  // Panel sits 90px above (or below for top-*) the host bubble. Use the
  // same corner as the host so the offset flips consistently. The
  // --pc-lift term tracks the launcher's mobile lift (widget-bridge) so
  // the panel never lands on top of the button it belongs to.
  let panelPos: string
  switch (position) {
    case 'bottom-right':
      panelPos = 'bottom: calc(90px + var(--pc-lift, 0px)); right: 20px;'
      break
    case 'bottom-left':
      panelPos = 'bottom: calc(90px + var(--pc-lift, 0px)); left: 20px;'
      break
    case 'top-right':
      panelPos = 'top: calc(90px + var(--pc-lift, 0px)); right: 20px;'
      break
    case 'top-left':
      panelPos = 'top: calc(90px + var(--pc-lift, 0px)); left: 20px;'
      break
  }
  return `
:host { all: initial; }
.pc-panel {
  position: fixed; ${panelPos}
  width: 360px; max-width: calc(100vw - 40px);
  height: 520px; max-height: calc(100vh - 120px);
  background: #fff; color: #1f2937;
  border-radius: ${radius}px;
  box-shadow: 0 12px 32px rgba(0,0,0,0.18);
  display: flex; flex-direction: column;
  font-family: system-ui, -apple-system, "Segoe UI", Roboto, sans-serif;
  font-size: 14px; line-height: 1.45;
  overflow: hidden;
}
.pc-header {
  display: flex; align-items: center; justify-content: space-between;
  padding: 10px 12px;
  background: ${primary}; color: #fff;
}
.pc-title-row { display: flex; align-items: center; gap: 8px; }
.pc-avatar {
  width: 28px; height: 28px; border-radius: 50%;
  object-fit: cover; flex: none;
  background: rgba(255,255,255,0.25);
}
.pc-title { font-weight: 700; font-size: 15px; }
.pc-status {
  font-size: 11px; padding: 2px 8px; border-radius: 999px;
  background: rgba(255,255,255,0.18); color: #fff;
}
.pc-status--connecting { background: #f59e0b; }
.pc-status--connected { background: #10b981; }
.pc-status--disconnected { background: #ef4444; }
.pc-status--error { background: #ef4444; }
.pc-close {
  background: transparent; border: 0; color: #fff;
  font-size: 22px; line-height: 1; cursor: pointer; padding: 0 6px;
}
.pc-close:hover { opacity: 0.85; }
.pc-list {
  flex: 1; overflow-y: auto; padding: 12px;
  background: #f9fafb; display: flex; flex-direction: column; gap: 8px;
}
.pc-msg { display: flex; flex-direction: column; gap: 2px; max-width: 80%; }
.pc-msg--visitor { align-self: flex-end; align-items: flex-end; }
.pc-msg--agent, .pc-msg--bot { align-self: flex-start; }
.pc-msg--system { align-self: center; }
.pc-bubble {
  padding: 8px 12px; border-radius: 12px;
  word-break: break-word; white-space: pre-wrap;
}
.pc-msg--visitor .pc-bubble { background: ${primary}; color: #fff; }
.pc-msg--agent .pc-bubble { background: #fff; color: #1f2937; border: 1px solid #e5e7eb; }
.pc-msg--bot .pc-bubble { background: #f3f4f6; color: #1f2937; }
.pc-meta { font-size: 10px; color: #6b7280; padding: 0 4px; }
.pc-msg--failed .pc-bubble { opacity: 0.6; }
.pc-retry {
  display: flex; align-items: center; gap: 6px;
  font-size: 11px; color: #ef4444; padding: 0 4px;
}
.pc-retry-btn {
  background: transparent; border: 0; padding: 0;
  font: inherit; font-weight: 600; color: #ef4444;
  text-decoration: underline; cursor: pointer;
}
.pc-retry-btn:hover { opacity: 0.8; }
.pc-system {
  align-self: center; font-size: 12px; color: #6b7280;
  background: #f3f4f6; padding: 6px 10px; border-radius: 8px;
  text-align: center; max-width: 90%;
}
.pc-system--hint {
  background: #eef2ff;
  color: #4338ca;
}
.pc-quick-questions {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}
.pc-quick-question {
  border: 1px solid rgba(0,0,0,0.08);
  background: #fff;
  color: #1f2937;
  border-radius: 999px;
  padding: 8px 12px;
  font: inherit;
  cursor: pointer;
  transition: border-color 120ms ease, color 120ms ease, transform 120ms ease;
}
.pc-quick-question:hover {
  border-color: ${primary};
  color: ${primary};
  transform: translateY(-1px);
}
/* Inline quick replies rendered under a flow message bubble. */
.pc-quick-replies {
  display: flex;
  flex-direction: column;
  gap: 6px;
  margin-top: 6px;
}
.pc-quick-reply {
  border: 1px solid ${primary};
  background: #fff;
  color: ${primary};
  border-radius: 12px;
  padding: 8px 12px;
  font: inherit;
  text-align: left;
  cursor: pointer;
  transition: background 120ms ease, color 120ms ease;
}
.pc-quick-reply:hover {
  background: ${primary};
  color: #fff;
}
.pc-composer-hint {
  padding: 8px 12px 0;
  font-size: 12px;
  color: #6b7280;
  background: #fff;
}
.pc-composer {
  display: flex; gap: 6px; padding: 8px;
  border-top: 1px solid #e5e7eb; background: #fff;
}
.pc-composer--disabled {
  opacity: 0.72;
}
.pc-input {
  flex: 1; min-width: 0;
  padding: 8px 10px; border: 1px solid #d1d5db; border-radius: 8px;
  font: inherit; color: inherit;
  background: #fff; outline: none;
}
.pc-input:focus { border-color: ${primary}; }
.pc-input:disabled {
  background: #f3f4f6;
  color: #9ca3af;
  cursor: not-allowed;
}
.pc-send {
  padding: 0 14px; background: ${primary}; color: #fff;
  border: 0; border-radius: 8px; font: inherit; font-weight: 600;
  cursor: pointer;
}
.pc-send:hover { filter: brightness(0.9); }
.pc-send:disabled { opacity: 0.5; cursor: not-allowed; }
`
}
